<template>
  <section
    v-if="sheets && sheets.length"
    class="rounded-lg border border-lightGray30 text-left"
    data-testid="ds-attendance-previous-sheets"
  >
    <button
      type="button"
      class="w-full flex items-center justify-between px-4 py-3"
      :aria-expanded="isOpen"
      @click="isOpen = !isOpen"
    >
      <span class="text-base font-bold text-textPrimary"
        >Folhas anteriores ({{ sheets.length }})</span
      >
      <v-icon :icon="isOpen ? mdiChevronUp : mdiChevronDown" size="22" />
    </button>
    <ul v-if="isOpen" class="border-t border-lightGray30 divide-y divide-lightGray30">
      <li
        v-for="sheet in sheets"
        :key="sheet.evidenceId"
        data-testid="ds-attendance-previous-sheet"
        class="flex flex-wrap items-center gap-3 px-4 py-3"
      >
        <v-icon :icon="mdiFileOutline" size="28" class="text-neutralLight" />
        <div class="flex flex-col min-w-0">
          <span class="text-sm font-medium text-textPrimary truncate">
            {{ sheet.fileName || 'Folha de monitoramento' }}
          </span>
          <span class="text-xs text-neutralLight">
            <template v-if="sheet.sentAt">Enviada em {{ formatDate(sheet.sentAt) }}</template>
            <template v-if="sheet.rejectedAt">
              · Recusada em {{ formatDate(sheet.rejectedAt) }}</template
            >
          </span>
        </div>
        <span
          class="rounded bg-negative/10 text-negative px-2 py-[2px] text-[11px] font-bold uppercase"
        >
          Recusada
        </span>
        <span v-if="sheet.rejectionReason" class="text-xs text-textPrimary">
          Motivo: {{ reasonLabel(sheet.rejectionReason) }}
        </span>
        <OutlineButton
          class="ml-auto"
          text="Abrir"
          width="80px"
          height="32px"
          :data-testid="`ds-attendance-previous-sheet-open-${sheet.evidenceId}`"
          @click="openSheet(sheet.evidenceId)"
        />
      </li>
    </ul>
  </section>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { mdiChevronDown, mdiChevronUp, mdiFileOutline } from '@mdi/js'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import Network from '@/Network'
import { useErrorToast } from '@/composables/useErrorToast'
import { REJECTION_REASON_LABELS, RejectionReason } from '@/constants/ds-attendance.constants'
import type { DSAttendancePreviousSheet } from '@/Network/Types/Responses/DsAttendancePolicyResponses'

/**
 * rejected-sheet-resubmission (G1, CA6): folhas recusadas, abaixo do visualizador.
 * "Abrir" usa a rota de anexo do registro (marca d'água por visualizador, anti-IDOR):
 * a folha recusada continua ligada ao registro, só deixou de ser a principal.
 */
const props = defineProps<{
  containerId: number | string
  sheets?: DSAttendancePreviousSheet[]
}>()

const { notifyError } = useErrorToast()
const isOpen = ref(true)

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })

const reasonLabel = (reason: string): string =>
  REJECTION_REASON_LABELS[reason as RejectionReason] ?? reason

async function openSheet(evidenceId: number): Promise<void> {
  try {
    const blob = await Network.dsAttendance.getAttachmentFile(props.containerId, evidenceId)
    window.open(URL.createObjectURL(blob), '_blank')
  } catch (error) {
    notifyError(error, 'Não foi possível abrir a folha anterior.')
  }
}
</script>
